import React, { useState, useRef } from 'react';
import axiosInstance from '../../../../axiosConfig';
import axios from 'axios';
import ChatUrl from './ChatUrl_for_suite';
import SummaryDisplay from './SummaryDisplayNews';
import styles from '../MicroProbeVideo.module.css';

const VideoInsight = () => {
  const [url, setUrl] = useState('');
  const [question, setQuestion] = useState('');
  const [summary, setSummary] = useState([]);
  const [loadingHighlights, setLoadingHighlights] = useState(false);
  const [loadingQuestion, setLoadingQuestion] = useState(false);
  const [loadingValidation, setLoadingValidation] = useState(false); // NEW: Separate state for validation
  const [conversationHistory, setConversationHistory] = useState([]);
  const [showChat, setShowChat] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const source = useRef(axios.CancelToken.source());
  const [isUrlSubmitted, setIsUrlSubmitted] = useState(false);
  
  // NEW: Multiple videos state
  const [videoList, setVideoList] = useState([]);
  
  const lastRequestTimeHighlights = useRef(0);
  const lastRequestTimeQuestion = useRef(0);
  const MIN_REQUEST_INTERVAL = 2000;

  const handleUrlChange = (event) => {
    setUrl(event.target.value);
    setResponseMessage('');
  };

  // NEW: Add single video to list
  const handleAddVideo = () => {
    if (!url.trim()) {
      setResponseMessage('Please enter a valid YouTube URL');
      return;
    }

    // Check if URL already exists
    const urlExists = videoList.some(video => video.url === url.trim());
    if (urlExists) {
      setResponseMessage('This video is already in the list');
      return;
    }

    const newVideo = {
      id: Date.now(),
      url: url.trim(),
      title: 'YouTube Video', // Will be updated if added from search
    };

    setVideoList([...videoList, newVideo]);
    setUrl('');
    setResponseMessage('');
  };

  // NEW: Handle video selection from ChatUrl component
  const handleVideoSelect = (videoUrl, videoTitle) => {
    // Check if URL already exists
    const urlExists = videoList.some(video => video.url === videoUrl);
    if (urlExists) {
      setResponseMessage('This video is already in the list');
      setTimeout(() => setResponseMessage(''), 3000);
      return;
    }

    const newVideo = {
      id: Date.now(),
      url: videoUrl,
      title: videoTitle,
    };

    setVideoList([...videoList, newVideo]);
    setResponseMessage(`✓ Video added: "${videoTitle}"`);
    setTimeout(() => setResponseMessage(''), 5000);
  };

  // NEW: Remove video from list
  const handleRemoveVideo = (videoId) => {
    setVideoList(videoList.filter(video => video.id !== videoId));
  };

  // NEW: Clear all videos
  const handleClearAll = () => {
    setVideoList([]);
    setConversationHistory([]);
    setQuestion('');
    setSummary([]);
    setResponseMessage('');
    setIsUrlSubmitted(false);
  };

  // NEW: Validate transcripts and submit multiple videos for analysis
  const handleSubmitVideos = async () => {
    if (videoList.length === 0) {
      setResponseMessage('Please add at least one video');
      return;
    }

    setResponseMessage('Validating video transcripts...');
    setLoadingValidation(true); // Use separate validation loading state

    try {
      // Validate each video has a transcript
      const videoUrls = videoList.map(v => v.url).join(',');
      
      const response = await axiosInstance.post(
        `/youtube_validate_transcripts/`,
        { urls: videoUrls }
      );

      if (response.data.error) {
        throw new Error(response.data.error);
      }

      const { valid_videos, invalid_videos } = response.data;

      // Remove invalid videos from the list
      if (invalid_videos.length > 0) {
        const validUrls = valid_videos.map(v => v.url);
        const updatedVideoList = videoList.filter(video => validUrls.includes(video.url));
        setVideoList(updatedVideoList);

        // Show error message for invalid videos
        const invalidTitles = invalid_videos.map(v => v.title || v.url).join(', ');
        setResponseMessage(
          `⚠️ Removed ${invalid_videos.length} video(s) without transcripts: ${invalidTitles}. ${
            updatedVideoList.length > 0 
              ? `Proceeding with ${updatedVideoList.length} valid video(s).` 
              : 'Please add videos with available transcripts.'
          }`
        );

        if (updatedVideoList.length === 0) {
          setLoadingValidation(false);
          return;
        }
      } else {
        setResponseMessage('✓ All videos validated successfully!');
      }

      // Proceed with analysis setup (but don't run analysis yet)
      setIsUrlSubmitted(true);
      setConversationHistory([]);
      setQuestion('');
      setSummary([]);
      
      // Clear success message after 3 seconds
      if (invalid_videos.length === 0) {
        setTimeout(() => setResponseMessage(''), 3000);
      }

    } catch (error) {
      console.error('Error validating transcripts:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to validate videos. Please try again.';
      setResponseMessage(`❌ ${errorMsg}`);
    } finally {
      setLoadingValidation(false);
    }
  };

  const handleKeyHighlightsClick = async () => {
    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestTimeHighlights.current;
    
    if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
      const waitTime = Math.ceil((MIN_REQUEST_INTERVAL - timeSinceLastRequest) / 1000);
      setResponseMessage(`Please wait ${waitTime} second${waitTime > 1 ? 's' : ''} before sending another request.`);
      return;
    }

    if (videoList.length === 0) {
      setResponseMessage('Please add at least one video');
      return;
    }

    setLoadingHighlights(true);
    setSummary(null);
    setResponseMessage('');
    
    lastRequestTimeHighlights.current = now;

    try {
      if (source.current) {
        source.current.cancel('Operation canceled by the user.');
      }
      source.current = axios.CancelToken.source();

      // NEW: Send multiple video URLs
      const videoUrls = videoList.map(v => v.url).join(',');
      
      const response = await axiosInstance.post(
        `/youtube_transcript_summary/?transcript=${encodeURIComponent(videoUrls)}`,
        {}, 
        {
          cancelToken: source.current.token,
        }
      );

      if (response.data.error) {
        throw new Error(response.data.error);
      }

      const parsedResponse = response.data.response;
      setSummary(parsedResponse);
      setLoadingHighlights(false);
    } catch (error) {
      console.error('Error fetching highlights:', error);
      
      if (axios.isCancel(error)) {
        console.log('Request canceled:', error.message);
      } else if (error.response && error.response.status === 429) {
        setResponseMessage('Please wait a moment before sending another request.');
      } else {
        const errorMsg = error.response?.data?.error || error.message || 'An error occurred. Please try again.';
        setResponseMessage(errorMsg);
      }
      setLoadingHighlights(false);
    }
  };

  const handleAskQuestionClick = async () => {
    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestTimeQuestion.current;
    
    if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
      const waitTime = Math.ceil((MIN_REQUEST_INTERVAL - timeSinceLastRequest) / 1000);
      setResponseMessage(`Please wait ${waitTime} second${waitTime > 1 ? 's' : ''} before sending another request.`);
      return;
    }

    if (videoList.length === 0 || !question) {
      setResponseMessage('Please add videos and enter a question');
      return;
    }

    setLoadingQuestion(true);
    setResponseMessage('');
    
    lastRequestTimeQuestion.current = now;

    try {
      if (source.current) {
        source.current.cancel('Operation canceled by the user.');
      }
      source.current = axios.CancelToken.source();

      // NEW: Send multiple video URLs
      const videoUrls = videoList.map(v => v.url).join(',');

      const response = await axiosInstance.post('/youtube_chat/', {
        question: question,
        chat_history: conversationHistory,
        url: videoUrls,
      }, {
        cancelToken: source.current.token,
      });

      if (response.data.error) {
        throw new Error(response.data.error);
      }

      setConversationHistory([...conversationHistory, { question: question, answer: response.data.answer }]);
      setQuestion('');
      setLoadingQuestion(false);
    } catch (error) {
      console.error('Error fetching answer:', error);
      
      if (axios.isCancel(error)) {
        console.log('Request canceled:', error.message);
      } else if (error.response && error.response.status === 429) {
        setResponseMessage('Please wait a moment before sending another request.');
      } else {
        const errorMsg = error.response?.data?.error || error.message || 'An error occurred. Please try again.';
        setResponseMessage(errorMsg);
      }
      setLoadingQuestion(false);
    }
  };

  const handleChatClick = () => {
    setShowChat(!showChat);
    if (showChat) {
      setResponseMessage('');
      lastRequestTimeQuestion.current = 0;
    }
  };

  return (
    <>
      <h3>Video Insight</h3>
      <ChatUrl onVideoSelect={handleVideoSelect} onClearVideos={handleClearAll} videoList={videoList} />
          
          <div className={styles.divider}> </div>
          
          {/* NEW: Video input and add button */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ fontSize: 'small' }}>Add YouTube URL: </label>
            <input
              type="text"
              value={url}
              onChange={handleUrlChange}
              placeholder="Enter YouTube URL or select from search above"
              className={styles.urlInput}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddVideo();
                }
              }}
            />
            <button onClick={handleAddVideo}>Add Video</button>
          </div>

          {/* NEW: Video list display */}
          {videoList.length > 0 && (
            <div style={{ 
              marginBottom: '15px', 
              padding: '10px', 
              background: '#f5f5f5', 
              borderRadius: '4px',
              maxHeight: '300px',
              overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                <strong style={{ fontSize: 'small', flex: 1 }}>
                  Videos to Analyze ({videoList.length})
                </strong>
                <button 
                  onClick={handleClearAll}
                  style={{ 
                    flexShrink: 0,
                    fontSize: 'small', 
                    padding: '4px 8px',
                    background: '#ff5252',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Clear All
                </button>
              </div>
              
              {videoList.map((video, index) => (
                <div 
                  key={video.id} 
                  style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: '8px',
                    marginBottom: '5px',
                    background: 'white',
                    borderRadius: '4px',
                    fontSize: 'small'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <strong>{index + 1}. {video.title}</strong>
                    <div style={{ color: '#666', fontSize: '0.85em', marginTop: '2px' }}>
                      {video.url.length > 60 ? video.url.substring(0, 60) + '...' : video.url}
                    </div>
                  </div>
                  <button 
                    onClick={() => handleRemoveVideo(video.id)}
                    style={{ 
                      marginLeft: '10px',
                      padding: '4px 8px',
                      background: '#ff5252',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: 'small'
                    }}
                  >
                    Remove
                  </button>
                </div>
              ))}
              
              <button 
                onClick={handleSubmitVideos}
                disabled={loadingValidation}
                style={{ 
                  marginTop: '10px',
                  width: '100%',
                  padding: '8px',
                  background: loadingValidation ? '#ccc' : '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loadingValidation ? 'not-allowed' : 'pointer',
                  fontSize: 'small',
                  fontWeight: 'bold'
                }}
              >
                {loadingValidation ? 'Validating...' : `Analyze ${videoList.length} Video${videoList.length > 1 ? 's' : ''}`}
              </button>
            </div>
          )}
          
          {responseMessage && (
            <div className={styles.errorMessage}>
              {responseMessage}
            </div>
          )}

          {isUrlSubmitted && (
            <div className={styles.actionsContainer}>
              <div>
                <button onClick={handleKeyHighlightsClick} disabled={loadingHighlights}>
                  Get Analysis
                </button>
                {loadingHighlights && <p>Analyzing {videoList.length} video{videoList.length > 1 ? 's' : ''}...</p>}
                {summary && summary.length > 0 && <SummaryDisplay summary={summary} />}
              </div>
              <div>
                <button onClick={handleChatClick}>Chat</button>
                {showChat && (
                  <div className={styles.chatContainer}>
                    {conversationHistory.length > 0 && (
                      <div>
                        <h3>Conversation History:</h3>
                        {conversationHistory.map((qaPair, index) => (
                          <div key={index} className={styles.conversationItem}>
                            <div>Q: {qaPair.question}</div>
                            <div className={styles.answerContainer}>
                              <SummaryDisplay summary={[qaPair.answer]} />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className={styles.questionInputContainer}>
                      <textarea
                        type="text"
                        value={question}
                        onChange={(e) => { setQuestion(e.target.value); setResponseMessage(''); }}
                        placeholder="Ask about the video(s)..."
                        className={styles.questionInput}
                      />
                      <button 
                        onClick={handleAskQuestionClick} 
                        disabled={loadingQuestion}
                      >
                        {loadingQuestion ? 'Loading...' : 'Ask Question'}
                      </button>
                      {loadingQuestion && <p>Analyzing your question...</p>}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
    </>
  );
};

export default VideoInsight;